import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  CANCEL_DISCORD_SERVER_CHANGE,
  CONFIRM_DISCORD_SERVER_CODE,
  GET_DISCORD_SERVER_SETTINGS,
  REQUEST_DISCORD_SERVER_CODE,
} from "../../../../queries/mutation/broadcaster/discordServerQueries";
import { GET_DISCORD_VIP_ROLE_SETTINGS } from "../../../../queries/mutation/broadcaster/vipQueries";

// Every mutation returns the fresh settings - write them straight into the
// query instead of refetching.
const writeSettings =
  (field: string) =>
  (cache: any, { data }: any) => {
    cache.writeQuery({
      query: GET_DISCORD_SERVER_SETTINGS,
      data: { getDiscordServerSettings: data?.[field] },
    });
  };

const DiscordServerSetting = () => {
  const { data, loading, error, refetch } = useQuery(
    GET_DISCORD_SERVER_SETTINGS,
    { fetchPolicy: "network-only" },
  );
  const [serverId, setServerId] = useState("");
  const [code, setCode] = useState("");

  const [requestCode, { loading: sending }] = useMutation(
    REQUEST_DISCORD_SERVER_CODE,
    { update: writeSettings("requestDiscordServerCode") },
  );
  const [confirmCode, { loading: confirming }] = useMutation(
    CONFIRM_DISCORD_SERVER_CODE,
    {
      update: writeSettings("confirmDiscordServerCode"),
      // The VIP role card lists the connected server's roles.
      refetchQueries: [{ query: GET_DISCORD_VIP_ROLE_SETTINGS }],
    },
  );
  const [cancelChange, { loading: cancelling }] = useMutation(
    CANCEL_DISCORD_SERVER_CHANGE,
    { update: writeSettings("cancelDiscordServerChange") },
  );

  const settings = data?.getDiscordServerSettings;
  const connectedServerId: string = settings?.serverId ?? "";
  const busy = sending || confirming || cancelling;

  // Shows the connected server, same as the Discord user ID field above
  // shows its saved value.
  useEffect(() => {
    setServerId(connectedServerId);
  }, [connectedServerId]);

  const handleSendCode = async () => {
    try {
      await requestCode({ variables: { serverId: serverId.trim() } });
      setCode("");
      toast.success("Code sent to the server's owner in Discord");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleConfirm = async () => {
    try {
      await confirmCode({ variables: { code: code.trim() } });
      setCode("");
      toast.success("Discord server connected");
    } catch (e: any) {
      toast.error(e.message);
      // A code that ran out of attempts is gone - show the send form again.
      refetch();
    }
  };

  const handleCancel = async () => {
    try {
      await cancelChange();
      setServerId(connectedServerId);
      setCode("");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="p-4 bg-base-200 rounded-lg">
      <h3 className="font-semibold text-base-content">Discord Server</h3>
      <p className="text-sm text-base-content/60 mt-1">
        Your community's server. The fan club VIP role and Discord display
        names come from here.
      </p>

      {loading ? (
        <span className="loading loading-dots loading-sm mt-3" />
      ) : error ? (
        <div className="flex items-center gap-2 mt-3">
          <p className="text-xs text-error">
            Couldn't load your Discord server: {error.message}
          </p>
          <button className="btn btn-xs" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : (
        <>
          {settings?.serverId ? (
            settings.botInServer ? (
              <p className="text-xs text-success mt-2">
                ✓ Connected to {settings.serverName}
              </p>
            ) : (
              <p className="text-xs text-warning mt-2">
                Connected to server {settings.serverId}, but the bot isn't in
                it -{" "}
                <a
                  className="link"
                  href={settings.botInviteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  add the bot
                </a>
              </p>
            )
          ) : (
            <p className="text-xs text-base-content/50 mt-2">
              No server connected
            </p>
          )}

          {settings?.pendingServerId ? (
            <div className="mt-3">
              <p className="text-sm">
                We DM'd a code to the owner of{" "}
                <span className="font-semibold">
                  {settings.pendingServerName ?? settings.pendingServerId}
                </span>
                . Enter it to finish connecting (expires{" "}
                {new Date(settings.codeExpiresAt).toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                })}
                ).
              </p>
              <div className="flex flex-col sm:flex-row gap-2 mt-2">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6-digit code"
                  className="input input-bordered flex-1"
                  disabled={busy}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleConfirm}
                  disabled={busy || !/^\d{6}$/.test(code.trim())}
                >
                  {confirming ? "Checking..." : "Confirm"}
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={handleCancel}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={serverId}
                  onChange={(e) => setServerId(e.target.value)}
                  placeholder="Server ID, e.g. 123456789012345678"
                  className="input input-bordered flex-1"
                  disabled={busy}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSendCode}
                  disabled={
                    busy ||
                    !serverId.trim() ||
                    serverId.trim() === connectedServerId
                  }
                >
                  {sending ? "Sending..." : "Send code"}
                </button>
              </div>
              <p className="text-xs text-base-content/50 mt-2">
                The bot has to be in the server first -{" "}
                <a
                  className="link"
                  href={settings?.botInviteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  add it to your server
                </a>
                . Then in Discord turn on Settings → Advanced → Developer
                Mode, right click your server's icon and pick Copy Server ID.
                The server's owner gets a code to confirm it's theirs.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DiscordServerSetting;
